import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { pathFor, mirrorPath } from '../routes/paths';
import { useLanguage } from '../contexts/LanguageContext';

const ACCENT = '#67313a';

const HeaderContainer = styled(motion.header)`
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 1000;
  background: rgba(255,255,255,1);
  transition: all 0.3s ease;
  padding: ${props => (props.compact ? '6px 0 8px' : '10px 0 18px')};
  box-shadow: 0 2px 18px rgba(0,0,0,0.06);
`;

const Nav = styled.nav`
  max-width: 1400px;
  margin: 10px auto;
  padding: 0 60px;
  height: 40px; /* ensure vertical centering zone */
  display: flex;
  justify-content: space-between;
  align-items: center; /* vertically center links within the header background */
  position: relative;
  @media (max-width: 968px) {
    padding: 0 40px;
  }
`;

const LeftMenu = styled.ul`
  display: flex;
  list-style: none;
  gap: 45px;
  align-items: center;
  flex: 1;

  @media (max-width: 968px) {
    display: none;
  }
`;

const Logo = styled.div`
  font-family: 'Playfair Display', serif;
  font-size: 36px;
  font-weight: 400;
  color: #2c2c2c;
  letter-spacing: 3px;
  transition: color 0.3s ease;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
`;

const LogoImage = styled.img`
  height: 40px;
  width: auto;
  display: ${props => (props.compact ? 'none' : 'block')};
  @media (max-width: 968px) {
    display: none;
  }
`;

const LogoText = styled.div`
  font-family: 'Playfair Display', serif;
  font-size: 22px;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: ${ACCENT};
  display: ${props => (props.compact ? 'block' : 'none')};
  @media (max-width: 968px) {
    display: block;
  }
`;

const BrandRow = styled.div`
  margin-top: 10px;
  display: ${props => (props.compact ? 'none' : 'flex')};
  justify-content: center;
  @media (max-width: 968px) {
    display: none;
  }
`;

const BrandName = styled.div`
  font-family: 'Playfair Display', serif;
  font-size: 20px;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: ${ACCENT};
`;

const RightMenu = styled.ul`
  display: flex;
  list-style: none;
  gap: 45px;
  align-items: center;
  flex: 1;
  justify-content: flex-end;

  @media (max-width: 968px) {
    display: none;
  }
`;

const NavLink = styled.li`
  font-family: 'Montserrat', sans-serif;
  font-size: 11px;
  font-weight: ${props => props.isActive ? '700' : '500'};
  letter-spacing: 2px;
  text-transform: uppercase;
  color: ${props => props.isActive ? ACCENT : '#7a7a7a'};
  cursor: pointer;
  transition: all 0.3s ease;
  opacity: ${props => props.isActive ? '1' : '0.9'};
  display: inline-flex; /* allow vertical alignment of text */
  align-items: center;
  transform: ${props => (props.compact ? 'none' : 'translateY(8px)')}; /* lower only in full header */
  
  &:hover {
    opacity: 1;
  }
`;

const Flag = styled.span`
  font-size: 16px;
  margin-left: 10px;
  display: inline-flex;
  align-items: center;
  transform: ${props => (props.compact ? 'none' : 'translateY(6px)')};
`;

/* Right-aligned mobile-only flag */
const MobileFlag = styled.span`
  display: none;
  @media (max-width: 968px) {
    display: inline-flex;
    position: absolute;
    right: 40px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 16px;
    cursor: pointer;
  }
`;

const MenuIcon = styled.div`
  display: none;
  flex-direction: column;
  gap: 4px;
  cursor: pointer;

  @media (max-width: 968px) {
    display: flex;
    position: absolute;
    left: 40px; /* move burger to left on mobile */
    top: 50%;
    transform: translateY(-50%);
  }

  span {
    width: 25px;
    height: 2px;
    background: #2c2c2c;
    transition: all 0.3s ease;
  }
`;

const MobileMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #ffffff;
  box-shadow: 0 12px 24px rgba(0,0,0,0.12);
  display: none;
  z-index: 1003; /* above backdrop */

  @media (max-width: 968px) {
    display: ${props => (props.open ? 'block' : 'none')};
  }
`;

/* Full-screen backdrop to close menu when tapping outside */
const Backdrop = styled.div`
  display: none;
  @media (max-width: 968px) {
    display: ${props => (props.open ? 'block' : 'none')};
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.2);
    z-index: 1002; /* below MobileMenu, above header content */
  }
`;

const MobileMenuList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 16px 24px 20px;
  display: grid;
  gap: 14px;
`;

const MobileItem = styled.li`
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #4a4a4a;
`;

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth <= 968 : false));
  const location = useLocation();
  const navigate = useNavigate();
  const { lang } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY || 0;
      setScrolled(y > 50);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 968);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // No scrollspy highlight; highlighting is route-based now

  const switchLang = () => {
    const p = location.pathname || '/';
    const targetLang = lang === 'fr' ? 'en' : 'fr';
    const target = mirrorPath(p, targetLang);
    navigate(target);
  };

  const goHomeSection = (id) => {
    const homePath = pathFor('home', lang);
    if (location.pathname !== homePath) {
      navigate(homePath);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 350);
    } else {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const flagEmoji = lang === 'fr' ? '🇬🇧' : '🇫🇷';

  const compact = scrolled || isMobile;

  return (
    <HeaderContainer id="site-header" compact={compact} initial={false}>
      <Nav>
        <LeftMenu>
          <NavLink compact={compact} isActive={location.pathname === '/' || location.pathname === '/fr'} onClick={() => goHomeSection('home')}>{lang === 'fr' ? 'Accueil' : 'Home'}</NavLink>
          <NavLink compact={compact} isActive={false} onClick={() => navigate(pathFor('experience', lang))}>{lang === 'fr' ? 'Expérience' : 'Experience'}</NavLink>
          <NavLink compact={compact} isActive={false} onClick={() => navigate(pathFor('portfolio', lang))}>Portfolio</NavLink>
        </LeftMenu>
        <Logo
          role="link"
          aria-label={lang === 'fr' ? 'Aller à l\'accueil' : 'Go to home'}
          tabIndex={0}
          onClick={() => navigate(pathFor('home', lang))}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(pathFor('home', lang)); } }}
        >
          <LogoImage compact={compact} src="/images/Monogramme LL2 Bordeaux.webp" alt="Monogramme LL" />
          <LogoText compact={compact}>Lino Ludovic</LogoText>
        </Logo>
        <RightMenu>
          <NavLink compact={compact} isActive={false} onClick={() => navigate(pathFor('about', lang))}>{lang === 'fr' ? 'À propos' : 'About'}</NavLink>
          <NavLink compact={compact} isActive={false} onClick={() => navigate(pathFor('videographer', lang))}>Film</NavLink>
          <NavLink compact={compact} isActive={false} onClick={() => navigate(pathFor('contact', lang))}>{lang === 'fr' ? 'Contact' : 'Contact'}</NavLink>
          <Flag compact={compact}
            role="button"
            aria-label={lang === 'fr' ? 'Switch to English' : 'Passer en français'}
            title={lang === 'fr' ? 'English version' : 'Version française'}
            onClick={switchLang}
            tabIndex={0}
          >{flagEmoji}</Flag>
        </RightMenu>
        <MenuIcon
          onClick={() => setMenuOpen(o => !o)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setMenuOpen(o => !o); } }}
          aria-label="Toggle menu"
          role="button"
          tabIndex={0}
        >
          <span></span>
          <span></span>
          <span></span>
        </MenuIcon>
        <MobileFlag
          role="button"
          aria-label={lang === 'fr' ? 'Switch to English' : 'Passer en français'}
          title={lang === 'fr' ? 'English version' : 'Version française'}
          onClick={switchLang}
          tabIndex={0}
        >{flagEmoji}</MobileFlag>
      </Nav>
      <BrandRow compact={compact}>
        <BrandName>Lino Ludovic</BrandName>
      </BrandRow>
      <Backdrop open={menuOpen} onClick={() => setMenuOpen(false)} />
      <MobileMenu open={menuOpen}>
        <MobileMenuList>
          <MobileItem onClick={() => { setMenuOpen(false); goHomeSection('home'); }}>{lang === 'fr' ? 'Accueil' : 'Home'}</MobileItem>
          <MobileItem onClick={() => { setMenuOpen(false); navigate(pathFor('experience', lang)); }}>{lang === 'fr' ? 'Expérience' : 'Experience'}</MobileItem>
          <MobileItem onClick={() => { setMenuOpen(false); navigate(pathFor('portfolio', lang)); }}>Portfolio</MobileItem>
          <MobileItem onClick={() => { setMenuOpen(false); navigate(pathFor('about', lang)); }}>{lang === 'fr' ? 'À propos' : 'About'}</MobileItem>
          <MobileItem onClick={() => { setMenuOpen(false); navigate(pathFor('videographer', lang)); }}>{lang === 'fr' ? 'Film' : 'Film'}</MobileItem>
          <MobileItem onClick={() => { setMenuOpen(false); navigate(pathFor('contact', lang)); }}>{lang === 'fr' ? 'Contact' : 'Contact'}</MobileItem>
        </MobileMenuList>
      </MobileMenu>
    </HeaderContainer>
  );
};

export default Header;
